import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiCall } from '../AuthContext';
import { Search, ShoppingBag, Store, Tag, Loader2, CheckCircle, AlertCircle, Plus, Camera, X, ExternalLink, Image as ImageIcon, Calculator, Printer, ChevronDown, ChevronUp, Package, Sparkles } from 'lucide-react';

export default function ProductForm() {
  const [batches, setBatches] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    batch_id: '', barcode: '', product_name: '', product_desc: '',
    quantity: 1, retailPrice: '', operations: '', ourPriceValue: '', finalPrice: '', cost: '',
    compareAtPrice: '', vendor: '', sku: '', weight: '', productType: '', squareCategory: '', shopifyShippingProfile: '',
    // Shopify "Physical product" toggle. ON by default for tangible goods;
    // toggle OFF for digital/services to skip shipping requirements.
    requiresShipping: true,
    // Local Pickup Only: stays physical + taxable (POS charges tax) but
    // checkout offers only store pickup (no shipping rates).
    localPickupOnly: false,
    ebayCategory: '', ebayCondition: 'NEW', ebayFulfillmentPolicy: '', ebayReturnPolicy: '', ebayPaymentPolicy: '', ebayShippingCost: '0', ebayReturns: true, ebayReturnDays: '30', ebayType: '', ebayBrand: '', ebayColor: '', ebayModel: '',
    ebayWeightLbs: '', ebayWeightOz: '', ebayDimL: '', ebayDimW: '', ebayDimH: '', ebayShippingMethod: 'USPSMedia',
    ebayItemSpecifics: [{ name: '', value: '' }],
    costMode: '$', costPercent: 28,
  });
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [samsLoading, setSamsLoading] = useState(false);
  const [samsOpen, setSamsOpen] = useState(false);
  const [samsItemNumber, setSamsItemNumber] = useState('');
  const [samsNameQuery, setSamsNameQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [lookupResult, setLookupResult] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [showStores, setShowStores] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [activeTab, setActiveTab] = useState('print');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [labelPreview, setLabelPreview] = useState(null);
  const [labelZpl, setLabelZpl] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushProductLoading, setPushProductLoading] = useState(false);
  const [savedProductId, setSavedProductId] = useState(null); // tracks auto-save so we don't dupe
  const lastSavedTotalRef = useRef(0); // last saved qty*price — used to delta-adjust the batch total on re-saves
  const autoSaveTimer = useRef(null); // debounce handle for name+price auto-save
  const [squareCategories, setSquareCategories] = useState([]);
  const [shopifyCategories, setShopifyCategories] = useState([]);
  const [shopifyShippingProfiles, setShopifyShippingProfiles] = useState([]);
  const [shopifySalesChannels, setShopifySalesChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState({});
  const [ebayCategories, setEbayCategories] = useState([]);
  const [ebaySearchQuery, setEbaySearchQuery] = useState('');
  const [ebayPolicies, setEbayPolicies] = useState({ fulfillment: [], return: [], payment: [] });
  const [ebaySoldData, setEbaySoldData] = useState(null);
  const [ebaySoldLoading, setEbaySoldLoading] = useState(false);
  const [ebayAspects, setEbayAspects] = useState([]);
  const navigate = useNavigate();

  const loadEbayAspects = async (categoryId) => {
    if (!categoryId) return;
    try {
      const res = await apiCall(`/api/ebay-item-specifics?category_id=${categoryId}`, { method: 'GET' });
      const aspects = res.data?.aspects || [];
      setEbayAspects(aspects);
      if (aspects.length > 0) {
        const newSpecifics = aspects.filter(a => a.required).map(a => ({
          name: a.name, value: a.values?.[0] || '', options: a.values || [], required: true
        }));
        if (newSpecifics.length) {
          setForm(prev => ({ ...prev, ebayItemSpecifics: newSpecifics }));
        }
      }
    } catch {}
  };
  const scannerRef = useRef(null);
  const scannerInstanceRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    apiCall('/api/batches', { method: 'GET' }).then(r => setBatches(r.data.batches || [])).catch(() => {});
    apiCall('/api/label-templates', { method: 'GET' }).then(r => setTemplates(r.data || [])).catch(() => {});
    apiCall('/api/square-categories', { method: 'GET' }).then(r => setSquareCategories(r.data || [])).catch(() => {});
    apiCall('/api/shopify-categories', { method: 'GET' }).then(r => setShopifyCategories(r.data?.result || [])).catch(() => {});
    apiCall('/api/shopify-shipping-profiles', { method: 'GET' }).then(r => setShopifyShippingProfiles(r.data?.profiles || [])).catch(() => {});
    apiCall('/api/shopify-sales-channels', { method: 'GET' }).then(r => {
      const channels = r.data?.channels || [];
      setShopifySalesChannels(channels);
    }).catch(() => {});
    apiCall('/api/ebay-categories', { method: 'GET' }).then(r => setEbayCategories(r.data || [])).catch(() => {});
    apiCall('/api/ebay-policies', { method: 'GET' }).then(r => setEbayPolicies(r.data || { fulfillment: [], return: [], payment: [], opted_in: false })).catch(() => {});
  }, []);

  // Prefill from ProductList / Dashboard "Quick Edit": pre-load the saved product and auto-run barcode lookup to fetch images
  useEffect(() => {
    const pre = location.state?.prefillProduct;
    if (!pre) return;
    setForm(prev => ({
      ...prev,
      batch_id: pre.batch_id || prev.batch_id,
      barcode: pre.barcode || '',
      product_name: pre.product_name || '',
      product_desc: pre.product_desc || '',
      quantity: pre.quantity ?? 1,
      retailPrice: pre.retail_price || '',
      finalPrice: pre.selling_price != null && Number(pre.selling_price) > 0 ? `$${Number(pre.selling_price).toFixed(2)}` : '',
    }));
    setMessage({ type: 'success', text: `Loaded ${pre.product_name || 'product'} for quick edit / reprint.` });
    // Editing an existing product? Seed the saved id + batch total so auto-save
    // UPDATES the original record instead of creating a duplicate.
    if (pre.product_id) {
      setSavedProductId(pre.product_id);
      const sp = Number(String(pre.selling_price ?? '').toString().replace(/[^0-9.]/g, '')) || 0;
      lastSavedTotalRef.current = sp * (pre.quantity ?? 1);
    }
    if (pre.barcode) {
      // Full barcode lookup — fetches fresh images, retail stores, all fields
      setTimeout(() => lookupBarcode(pre.barcode, { keepSaved: !!pre.product_id }), 50);
    } else if (pre.image_url || pre.product_name) {
      // Fallback: at least show the Product Found card so the user sees the saved image
      setLookupResult({
        found: true,
        product_title: pre.product_name || '',
        product_description: pre.product_desc || '',
        product_images: pre.image_url ? [pre.image_url] : [],
        retail_price: pre.retail_price || '',
        category: '',
        stores: [],
      });
    }
    // Clear navigation state so a refresh doesn't re-prefill
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => { return () => { if (scannerInstanceRef.current) try { scannerInstanceRef.current.stop(); } catch {} }; }, []);
  useEffect(() => {
    if (shopifySalesChannels.length === 0) return;
    const hasImages = lookupResult?.product_images?.length > 0;
    const defaults = {};
    shopifySalesChannels.forEach(ch => {
      const name = ch.name.toLowerCase();
      if (name.includes('point of sale') || name.includes('pos')) {
        defaults[ch.id] = true;
      } else if (name.includes('online store') && hasImages) {
        defaults[ch.id] = true;
      } else {
        defaults[ch.id] = false;
      }
    });
    setSelectedChannels(defaults);
  }, [shopifySalesChannels, lookupResult]);

  // Auto-save the product as soon as it has a name + a price (retail or sale)
  // and a batch is selected — no Print/Push required. Re-fires on price/name
  // edits so the DB record always reflects the latest values (debounced).
  useEffect(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    const hasName = (form.product_name || '').trim().length > 0;
    const hasPrice = !!(form.finalPrice || form.retailPrice);
    if (!form.batch_id || !hasName || !hasPrice || !form.quantity) return;
    autoSaveTimer.current = setTimeout(() => {
      ensureProductSaved({ silent: true }).catch(() => {});
    }, 1000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.product_name, form.finalPrice, form.retailPrice, form.batch_id, form.quantity, form.product_desc, form.barcode]);

  const lookupBarcode = async (barcodeValue, opts = {}) => {
    const code = barcodeValue || form.barcode;
    if (!code.trim()) return;
    // New scan → forget the previously saved product so edits create/update the right record.
    // A prefill-driven lookup (quick edit / reprint) passes keepSaved so we keep updating the original.
    if (!opts.keepSaved) { setSavedProductId(null); lastSavedTotalRef.current = 0; }
    setBarcodeLoading(true); setLookupResult(null); setMessage(null);
    try {
      const res = await apiCall(`/api/barcode-lookup?barcode=${encodeURIComponent(code.trim())}`, { method: 'GET' });
      const data = res.data;
      if (data.found) {
        setLookupResult(data);
        const numericPrice = data.retail_price ? data.retail_price.replace(/[^0-9.]/g, '') : '';
        const title = data.product_title || '';
        const desc = data.product_description || '';
        const category = data.category || '';
        
        // Extract brand from title (first word or two before product type)
        const titleWords = title.split(' ');
        let brand = '';
        let model = '';
        let color = '';
        let type = '';
        
        // Common color words
        const colors = ['Black','White','Red','Blue','Green','Yellow','Pink','Purple','Orange','Brown','Gray','Grey','Silver','Gold','Navy','Beige','Cream','Tan','Multi','Multicolor','Clear'];
        // Try to find color in title
        for (const c of colors) {
          if (title.toLowerCase().includes(c.toLowerCase())) { color = c; break; }
        }
        
        // Extract brand — usually first 1-2 words
        if (titleWords.length >= 2) {
          // If first word is all caps or capitalized and short, it's likely a brand
          brand = titleWords[0];
          if (titleWords[1] && /^[A-Z]/.test(titleWords[1]) && !colors.includes(titleWords[1])) {
            brand = titleWords[0] + ' ' + titleWords[1];
          }
        }
        
        // Extract type from category
        if (category) {
          const catParts = category.split('>').map(s => s.trim());
          type = catParts[catParts.length - 1] || '';
        }
        
        // Model — remaining words after brand (simplified)
        if (brand && title.startsWith(brand)) {
          model = title.slice(brand.length).trim().split(':')[0].trim().slice(0, 50);
        }
        
        // Weight from lookup (in lbs) — prefill Shopify weight + eBay weight split
        const wLbs = parseFloat(data.weight_lbs || '');
        let shopifyWeight = '';
        let ebayLbs = '';
        let ebayOz = '';
        if (!isNaN(wLbs) && wLbs > 0) {
          shopifyWeight = String(wLbs);
          ebayLbs = String(Math.floor(wLbs));
          ebayOz = String(Math.round((wLbs - Math.floor(wLbs)) * 16));
        }
        
        setForm(prev => ({
          ...prev, barcode: code.trim(), product_name: title,
          product_desc: desc,
          retailPrice: numericPrice ? '$' + numericPrice : '',
          compareAtPrice: numericPrice ? '$' + numericPrice : '',
          // Auto-fill the Sale Price + Cost so quick "Print" / "Push" actions don't store $0
          finalPrice: (!prev.finalPrice && numericPrice) ? '$' + parseFloat(numericPrice).toFixed(2) : prev.finalPrice,
          cost: (!prev.cost && numericPrice) ? '$' + (parseFloat(numericPrice) * ((Number(prev.costPercent) || 28) / 100)).toFixed(2) : prev.cost,
          sku: prev.sku || code.trim(),
          weight: shopifyWeight || prev.weight,
          ebayWeightLbs: ebayLbs || prev.ebayWeightLbs,
          ebayWeightOz: ebayOz || prev.ebayWeightOz,
          ebayBrand: brand || prev.ebayBrand,
          ebayColor: color || prev.ebayColor,
          ebayModel: model || prev.ebayModel,
          ebayType: type || prev.ebayType,
        }));
        
        // Auto-search eBay category based on product name
        try {
          const catRes = await apiCall(`/api/ebay-categories?q=${encodeURIComponent(title.slice(0, 50))}`, { method: 'GET' });
          if (catRes.data?.length) {
            setEbayCategories(catRes.data);
            setForm(prev => ({ ...prev, ebayCategory: catRes.data[0].id }));
            setEbaySearchQuery(title.slice(0, 30));
            // Load required item specifics for the category
            await loadEbayAspects(catRes.data[0].id);
          }
        } catch {}
        
        setMessage({ type: 'success', text: `Found: ${title}` });
      } else {
        setMessage({ type: 'info', text: data.message || 'Product not found. Enter details manually.' });
      }
    } catch { setMessage({ type: 'error', text: 'Barcode lookup failed.' }); }
    finally { setBarcodeLoading(false); }
  };

  const lookupSams = async () => {
    const item = (samsItemNumber || '').trim();
    const name = (samsNameQuery || '').trim();
    if (!item && !name) {
      setMessage({ type: 'error', text: "Enter a Sam's item number or product name first." });
      return;
    }
    setSamsLoading(true); setMessage(null);
    try {
      const params = new URLSearchParams();
      if (item) params.append('item_number', item);
      if (name) params.append('name', name);
      const res = await apiCall(`/api/samsclub-lookup?${params.toString()}`, { method: 'GET' });
      const data = res.data;
      if (data.found) {
        setLookupResult(data);
        const numericPrice = String(data.retail_price || '').replace(/[^0-9.]/g, '');
        const retailNum = parseFloat(numericPrice) || 0;
        // Fill the main form with Sam's data — preserve user's barcode if they had one
        setForm(p => ({
          ...p,
          // Upgrade barcode to Sam's real GTIN only if user didn't type one
          barcode: p.barcode || data.barcode || (item ? `SC-${item}` : ''),
          product_name: data.product_title || p.product_name,
          product_desc: data.product_description || p.product_desc,
          retailPrice: numericPrice ? '$' + numericPrice : p.retailPrice,
          // Default the Sale Price to the retail (user can adjust with ops or manually)
          finalPrice: (!p.finalPrice && retailNum > 0) ? '$' + retailNum.toFixed(2) : p.finalPrice,
          // Default Cost = retail * cost% so the cost field also autofills
          cost: (!p.cost && retailNum > 0) ? '$' + (retailNum * ((Number(p.costPercent) || 28) / 100)).toFixed(2) : p.cost,
          compareAtPrice: (!p.compareAtPrice && numericPrice) ? numericPrice : p.compareAtPrice,
        }));
        const warn = data.source === 'samsclub_search_by_name'
          ? ' (matched via name search — please verify)'
          : '';
        setMessage({ type: 'success', text: `Sam's: ${data.product_title || 'Product found'}${warn}` });
      } else {
        setMessage({ type: 'info', text: data.message || "Not found on Sam's Club." });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || "Sam's lookup failed." });
    } finally {
      setSamsLoading(false);
    }
  };

  const startCamera = useCallback(async () => {
    setShowCamera(true);
    setTimeout(async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (scannerRef.current) {
          const scanner = new Html5Qrcode("barcode-scanner");
          scannerInstanceRef.current = scanner;
          await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 280, height: 120 } },
            (decodedText) => { setForm(p => ({ ...p, barcode: decodedText })); scanner.stop().catch(() => {}); setShowCamera(false); scannerInstanceRef.current = null; lookupBarcode(decodedText); }, () => {});
        }
      } catch { setMessage({ type: 'error', text: 'Camera not available.' }); setShowCamera(false); }
    }, 200);
  }, []);

  const stopCamera = () => { if (scannerInstanceRef.current) scannerInstanceRef.current.stop().catch(() => {}); scannerInstanceRef.current = null; setShowCamera(false); };

  const calculatePrice = async () => {
    if (!form.retailPrice || !form.operations || !form.ourPriceValue) { setMessage({ type: 'error', text: 'Fill retail price, operation, and value to calculate.' }); return; }
    try {
      const res = await apiCall('/api/calculate-price', { method: 'POST', data: { retailPrice: form.retailPrice, operation: form.operations, value: parseFloat(form.ourPriceValue) } });
      const fp = formatMoney(res.data.finalPrice);
      const co = formatMoney(res.data.cost);
      setForm(p => ({ ...p, finalPrice: '$' + fp, cost: '$' + co }));
      // Auto-save product whenever a price has been calculated, so the Selling Price
      // is always recorded in Products without requiring a Print/Push action first.
      // Requires batch + product_name + quantity to be set; otherwise silently skip.
      // Pass the freshly calculated price as an override — React state hasn't flushed yet.
      setTimeout(() => {
        if (form.batch_id && form.product_name && form.quantity) {
          ensureProductSaved({ printed: false, channelUsed: null, overrides: { finalPrice: '$' + fp, cost: '$' + co } }).catch(() => {});
        }
      }, 0);
    } catch { setMessage({ type: 'error', text: 'Price calculation failed.' }); }
  };

  const handlePrintLabel = async () => {
    if (!form.product_name) { setMessage({ type: 'error', text: 'Product name required.' }); return; }
    setPrintLoading(true); setLabelPreview(null); setLabelZpl(null); setMessage(null);
    try {
      const ourPrice = form.finalPrice ? '$' + form.finalPrice : (form.retailPrice || '$0');
      const res = await apiCall('/api/print-label', { method: 'POST', data: {
        product_name: form.product_name, retail_price: form.retailPrice || '$0',
        our_price: ourPrice, barcode: form.barcode || '000000000000', template_id: selectedTemplate,
      }});
      if (res.data && !res.data.error && res.data.image) {
        setLabelPreview(res.data.image);
        setLabelZpl(res.data.zpl || '');
        // Auto-save product so it shows up on /product-list
        const pid = await ensureProductSaved({ printed: true });
        setMessage({
          type: 'success',
          text: pid
            ? `Label generated & product saved (ID: ${pid}). Click Print Label below.`
            : 'Label generated! Click Print Label below the preview.',
        });
      } else {
        setMessage({ type: 'error', text: res.data?.message || 'Failed to generate label. Try again.' });
      }
    } catch (e) {
      console.error('Print label error:', e);
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Print failed. Please try again.' });
    }
    finally { setPrintLoading(false); }
  };

  const handlePushSquare = async () => {
    if (!form.barcode || !form.product_name) { setMessage({ type: 'error', text: 'Barcode and product name required.' }); return; }
    setPushLoading(true);
    try {
      const res = await apiCall('/api/push-square', { method: 'POST', data: {
        barCode: form.barcode, productName: form.product_name, Desc: form.product_desc,
        categories: form.squareCategory, finalPrice: form.finalPrice || '0', quantity: form.quantity,
        squareImageUrl: lookupResult?.product_images || [],
        weight: form.weight || '0', sku: form.sku || form.barcode,
      }});
      const pid = await ensureProductSaved({ channelUsed: 'square' });
      setMessage({ type: 'success', text: (res.data.message || 'Pushed to Square') + (pid ? ` · Product saved (${pid})` : '') });
    } catch (e) { setMessage({ type: 'error', text: e.response?.data?.detail || 'Push to Square failed.' }); }
    finally { setPushLoading(false); }
  };

  const handlePushShopify = async () => {
    if (!form.barcode || !form.product_name || !form.finalPrice || !form.vendor || !form.sku || !form.quantity) {
      setMessage({ type: 'error', text: 'Fill all required Shopify fields (barcode, name, price, vendor, SKU, quantity).' }); return;
    }
    setPushLoading(true);
    try {
      const res = await apiCall('/api/push-shopify', { method: 'POST', data: {
        barCode: form.barcode, productName: form.product_name, Desc: form.product_desc,
        productType: form.productType, finalPrice: form.finalPrice, quantity: form.quantity,
        compareAtPrice: form.compareAtPrice, cost: form.cost, vendor: form.vendor,
        sku: form.sku, weight: form.weight, squareImageUrl: lookupResult?.product_images || [],
        shippingProfileId: form.shopifyShippingProfile || '',
        requiresShipping: form.requiresShipping,
        localPickupOnly: form.localPickupOnly,
        salesChannels: Object.entries(selectedChannels).filter(([_, v]) => v).map(([id]) => id),
      }});
      const diag = res.data?.publish_diagnostic;
      const pubs = diag?.published_to || [];
      const found = diag?.publications_found || [];
      if (diag && pubs.length === 0) {
        if (diag.publications_denied) {
          setMessage({ type: 'error', text: 'Shopify: product created but app is missing the "read_publications" scope. No sales channel assigned. Reconnect Shopify to grant the scope.' });
        } else if (!found.some(n => (n || '').includes('pos') || (n || '').includes('point of sale'))) {
          setMessage({ type: 'error', text: `Shopify: product created, but your store has no Point-of-Sale channel (found: ${found.join(', ') || 'none'}). Install Shopify POS (free) to enable POS-only listings.` });
        } else {
          setMessage({ type: 'error', text: `Shopify: product created, but publish to POS failed. ${(diag.errors || []).join('; ') || ''}` });
        }
      } else {
        const suffix = pubs.length ? ` · Published to: ${pubs.join(', ')}` : '';
        const lp = res.data?.local_pickup;
        const pickupNote = lp ? (lp.assigned ? ' · Local Pickup Only applied' : ` · WARNING: Pickup-only NOT applied (${lp.error})`) : '';
        const pid = await ensureProductSaved({ channelUsed: 'shopify' });
        setMessage({ type: lp && !lp.assigned ? 'error' : 'success', text: (res.data.message || 'Pushed') + suffix + pickupNote + (pid ? ` · Product saved (${pid})` : '') });
      }
    } catch (e) { setMessage({ type: 'error', text: e.response?.data?.detail || 'Push to Shopify failed.' }); }
    finally { setPushLoading(false); }
  };

  const handlePushEbay = async () => {
    if (!form.product_name || !form.finalPrice) { setMessage({ type: 'error', text: 'Product name and price required for eBay.' }); return; }
    setPushLoading(true);
    try {
      const res = await apiCall('/api/push-ebay', { method: 'POST', data: {
        barCode: form.barcode, productName: form.product_name, Desc: form.product_desc,
        finalPrice: form.finalPrice || '0', quantity: form.quantity,
        sku: form.sku || form.barcode, categoryId: form.ebayCategory || '',
        condition: form.ebayCondition || 'NEW',
        imageUrls: lookupResult?.product_images || [],
        shippingCost: form.ebayShippingCost || '0',
        returnsAccepted: form.ebayReturns !== false,
        returnDays: form.ebayReturnDays || '30',
        itemType: form.ebayType || 'Other',
        brand: form.ebayBrand || 'Unbranded',
        color: form.ebayColor || 'Multicolor',
        model: form.ebayModel || 'N/A',
        weightLbs: form.ebayWeightLbs || '',
        weightOz: form.ebayWeightOz || '',
        dimLength: form.ebayDimL || '',
        dimWidth: form.ebayDimW || '',
        dimHeight: form.ebayDimH || '',
        shippingMethod: form.ebayShippingMethod || 'USPSMedia',
        customSpecifics: (form.ebayItemSpecifics || []).filter(s => s.name && s.value),
      }});
      const pid = await ensureProductSaved({ channelUsed: 'ebay' });
      setMessage({ type: 'success', text: (res.data.message || 'Pushed to eBay') + (pid ? ` · Product saved (${pid})` : '') });
    } catch (e) { setMessage({ type: 'error', text: e.response?.data?.detail || 'Push to eBay failed.' }); }
    finally { setPushLoading(false); }
  };

  const ensureProductSaved = async ({ channelUsed = null, printed = false, overrides = {}, silent = false } = {}) => {
    const f = { ...form, ...overrides };
    if (!f.batch_id || !f.product_name || !f.quantity) {
      if (!silent) setMessage({ type: 'error', text: 'Batch, product name, and quantity are required to save the product.' });
      return null;
    }
    if (!silent) setPushProductLoading(true);
    try {
      // Strip "$" prefix and any commas from money fields before parseFloat
      // (parseFloat("$5.00") returns NaN — common bug that caused selling_price=0).
      const moneyToNum = (s) => {
        const n = parseFloat(String(s ?? '').replace(/[$,\s]/g, ''));
        return Number.isFinite(n) ? n : 0;
      };
      const sellNum = moneyToNum(f.finalPrice);
      const retailNum = moneyToNum(f.retailPrice);
      // Fallback: if no calculated/entered selling price yet, use the retail price
      // so the saved product never has selling_price=$0 when an MSRP is known.
      const finalSellingPrice = sellNum > 0 ? sellNum : retailNum;
      const qtyNum = parseInt(f.quantity) || 0;

      // Already auto-saved (e.g. right after Calculate Price)? UPDATE it with the
      // user's latest edits (title, prices, qty). Push flags are only set when an
      // actual channel push happens — the batch total counts pushed products only.
      if (savedProductId) {
        await apiCall(`/api/products/${savedProductId}`, { method: 'PUT', data: {
          product_name: f.product_name,
          product_desc: f.product_desc,
          barcode: f.barcode,
          quantity: qtyNum,
          selling_price: finalSellingPrice,
          retail_price: f.retailPrice,
          ...(printed ? { print_label: true } : {}),
          ...(channelUsed === 'shopify' ? { push_shopify: true } : {}),
          ...(channelUsed === 'square' ? { push_square: true } : {}),
          ...(channelUsed === 'ebay' ? { push_ebay: true } : {}),
        }});
        return savedProductId;
      }

      const payload = {
        batch_id: f.batch_id,
        product_name: f.product_name,
        product_desc: f.product_desc,
        barcode: f.barcode,
        quantity: qtyNum,
        selling_price: finalSellingPrice,
        retail_price: f.retailPrice,
        images: lookupResult?.product_images || [],
        print_label: printed,
        push_shopify: channelUsed === 'shopify',
        push_square: channelUsed === 'square',
        push_ebay: channelUsed === 'ebay',
      };
      const res = await apiCall('/api/products', { method: 'POST', data: payload });
      const pid = res.data?.product?.product_id;
      if (pid) setSavedProductId(pid);
      return pid;
    } catch (e) {
      if (!silent) setMessage({ type: 'error', text: `Auto-save failed: ${e.response?.data?.detail || e.message}` });
      return null;
    } finally {
      if (!silent) setPushProductLoading(false);
    }
  };

  // Compatibility wrapper — unused now that the dedicated Push Product button is removed,
  // but kept available for future re-introduction of a manual save action.
  // eslint-disable-next-line no-unused-vars
  const _handlePushProduct = async () => {
    const pid = await ensureProductSaved();
    if (pid) {
      setMessage({ type: 'success', text: `Product saved! ID: ${pid}` });
      setForm(p => ({ ...p, barcode: '', product_name: '', product_desc: '', quantity: 1, retailPrice: '', finalPrice: '', cost: '', compareAtPrice: '', vendor: '', sku: '', weight: '', operations: '', ourPriceValue: '' }));
      setLookupResult(null); setLabelPreview(null); setLabelZpl(null); setSavedProductId(null);
    }
  };

  const formatMoney = (val) => {
    const num = parseFloat(String(val).replace(/[^0-9.]/g, ''));
    return isNaN(num) ? '' : num.toFixed(2);
  };

  const handleChange = (field, value) => {
    setForm(p => {
      const next = { ...p, [field]: value };
      if (field === 'retailPrice') {
        const clean = value.replace(/[^0-9.]/g, '');
        next.retailPrice = clean ? '$' + clean : '';
        next.compareAtPrice = clean ? '$' + clean : '';
        // Auto-update cost if user is in % mode (cost is a fixed % of Retail Price)
        const rp = parseFloat(clean);
        if (!isNaN(rp) && rp > 0 && p.costMode === '%') {
          const pct = Number(p.costPercent) || 0;
          next.cost = '$' + formatMoney(rp * (pct / 100));
        }
      }
      if (field === 'finalPrice') {
        const clean = value.replace(/[^0-9.]/g, '');
        next.finalPrice = clean ? '$' + formatMoney(clean) : '';
      }
      if (field === 'compareAtPrice') {
        const clean = value.replace(/[^0-9.]/g, '');
        next.compareAtPrice = clean ? '$' + clean : '';
      }
      if (field === 'cost') {
        const clean = value.replace(/[^0-9.]/g, '');
        next.cost = clean ? '$' + clean : '';
      }
      if (field === 'costPercent') {
        const pct = Math.max(0, Math.min(100, Number(value) || 0));
        next.costPercent = pct;
        const rpNum = parseFloat(String(p.retailPrice).replace(/[^0-9.]/g, ''));
        if (p.costMode === '%' && !isNaN(rpNum) && rpNum > 0) {
          next.cost = '$' + formatMoney(rpNum * (pct / 100));
        }
      }
      if (field === 'costMode') {
        next.costMode = value;
        const rpNum = parseFloat(String(p.retailPrice).replace(/[^0-9.]/g, ''));
        if (value === '%' && !isNaN(rpNum) && rpNum > 0) {
          const pct = Number(p.costPercent) || 28;
          next.cost = '$' + formatMoney(rpNum * (pct / 100));
        }
      }
      return next;
    });
  };

  const tabs = [
    { key: 'print', label: 'Print', icon: Printer },
    { key: 'shopify', label: 'Shopify', icon: ShoppingBag },
    { key: 'square', label: 'Square', icon: Store },
    { key: 'ebay', label: 'eBay', icon: Tag },
  ];

  return (
    <div className="space-y-5 max-w-5xl" data-testid="product-form-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">X LabelSync Pro</h2>
          <p className="text-sm text-slate-500 mt-1">Scan barcode, calculate price, print label, or push to Shopify/Square/eBay.</p>
        </div>
        <button onClick={() => navigate('/quick-list')}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg font-semibold text-sm bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white shadow"
          data-testid="quick-list-toggle-btn"
          title="Open the simplified single-screen listing flow (mobile-friendly)">
          <Sparkles className="w-4 h-4" />
          Quick List
        </button>
      </div>

      {message && (
        <div className={`flex items-center gap-2 p-3 rounded-xl border text-sm ${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : message.type === 'info' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid="product-form-message">
          {message.type === 'success' ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
          <p className="font-medium">{message.text}</p>
        </div>
      )}

      {/* Barcode Section */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="barcode-section">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-[#0052FF]/10 flex items-center justify-center"><Search className="w-4 h-4 text-[#0052FF]" /></div>
          <div><h3 className="font-semibold text-[#0F172A]">Barcode Lookup</h3><p className="text-xs text-slate-500">Scan or type to fetch product details</p></div>
        </div>
        <div className="flex gap-2">
          <input value={form.barcode} onChange={e => handleChange('barcode', e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupBarcode(); } }}
            className="flex-1 border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent outline-none text-sm font-mono"
            placeholder="049000042566" data-testid="barcode-input" autoFocus />
          <button type="button" onClick={() => lookupBarcode()} disabled={barcodeLoading}
            className="inline-flex items-center gap-1.5 bg-[#0F172A] hover:bg-slate-800 text-white font-medium rounded-lg px-4 py-2 text-sm disabled:opacity-50" data-testid="barcode-lookup-btn">
            {barcodeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Lookup
          </button>
          <button type="button" onClick={showCamera ? stopCamera : startCamera}
            className={`inline-flex items-center gap-1.5 font-medium rounded-lg px-3 py-2 text-sm ${showCamera ? 'bg-red-500 text-white' : 'bg-[#0052FF] text-white'}`} data-testid="barcode-camera-btn">
            {showCamera ? <X className="w-4 h-4" /> : <Camera className="w-4 h-4" />} {showCamera ? 'Stop' : 'Scan'}
          </button>
        </div>
        {showCamera && <div className="mt-3 rounded-lg overflow-hidden border" data-testid="barcode-camera-view"><div id="barcode-scanner" ref={scannerRef} style={{ width: '100%' }} /><p className="text-center text-xs text-slate-500 py-1">Point camera at barcode</p></div>}
      </div>

      {/* Sam's Club Lookup Section */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="samsclub-section">
        <button
          type="button"
          onClick={() => setSamsOpen(o => !o)}
          className="w-full flex items-center gap-3 p-5 text-left"
          data-testid="samsclub-toggle"
        >
          <div className="w-9 h-9 rounded-lg bg-[#0057A4]/10 flex items-center justify-center">
            <Package className="w-4 h-4 text-[#0057A4]" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-[#0F172A]">Search Sam&apos;s Club</h3>
            <p className="text-xs text-slate-500">Find products by Sam&apos;s item number (e.g. P03011003) or product name — fills the form below</p>
          </div>
          {samsOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>

        {samsOpen && (
          <div className="px-5 pb-5 space-y-3" data-testid="samsclub-panel">
            <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-2">
              <input
                value={samsItemNumber}
                onChange={e => setSamsItemNumber(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupSams(); } }}
                className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#0057A4] focus:border-transparent outline-none text-sm font-mono"
                placeholder="Item # (e.g. P03011003)"
                data-testid="samsclub-item-input"
              />
              <input
                value={samsNameQuery}
                onChange={e => setSamsNameQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupSams(); } }}
                className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#0057A4] focus:border-transparent outline-none text-sm"
                placeholder="Or product name (e.g. Coca-Cola 16.9oz 24-pack)"
                data-testid="samsclub-name-input"
              />
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Tip: enter both for best results. If your manifest ID doesn&apos;t match Sam&apos;s public ID (common with liquidators), the product name is used to find a match.
              </p>
              <button
                type="button"
                onClick={lookupSams}
                disabled={samsLoading}
                className="inline-flex items-center gap-1.5 bg-[#0057A4] hover:bg-[#00467e] text-white font-medium rounded-lg px-4 py-2 text-sm disabled:opacity-50"
                data-testid="samsclub-search-btn"
              >
                {samsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search Sam&apos;s
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Lookup Result */}
      {lookupResult && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="lookup-result-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[#0F172A]">Product Found</h3>
            <div className="flex gap-2">
              {lookupResult.product_images?.length > 0 && <button type="button" onClick={() => setShowImages(!showImages)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-[#0052FF]/10 text-[#0052FF]" data-testid="toggle-images-btn"><ImageIcon className="w-3 h-3" /> {lookupResult.product_images.length} Images</button>}
              {lookupResult.stores?.length > 0 && <button type="button" onClick={() => setShowStores(!showStores)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-700" data-testid="toggle-stores-btn"><Store className="w-3 h-3" /> {lookupResult.stores.length} Stores</button>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            {lookupResult.product_images?.[0] && <img src={lookupResult.product_images[0]} alt="" className="w-14 h-14 rounded-lg object-contain border bg-white" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0F172A] truncate">{lookupResult.product_title}</p>
              <p className="text-xs text-slate-500">{lookupResult.category}</p>
              {(lookupResult.product_url || lookupResult.item_number) && (
                <a
                  href={lookupResult.product_url || `https://www.samsclub.com/p/-/${lookupResult.item_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-1 text-xs font-medium text-[#0057A4] hover:underline"
                  data-testid="samsclub-product-url"
                  title="Open the original Sam's Club product page in a new tab"
                >
                  Verify on Sam&apos;s Club <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            {lookupResult.retail_price && <div className="px-3 py-1 rounded-lg bg-green-50 border border-green-200"><p className="text-xs text-green-600">Retail</p><p className="text-lg font-bold text-green-700">{lookupResult.retail_price}</p></div>}
          </div>
          {showImages && lookupResult.product_images?.length > 0 && (
            <div className="mb-3 p-3 bg-slate-50 rounded-lg border" data-testid="product-images-panel">
              <div className="flex justify-between mb-2"><p className="text-sm font-semibold">Images</p><button type="button" onClick={() => setShowImages(false)}><X className="w-4 h-4 text-slate-400" /></button></div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">{lookupResult.product_images.map((img, i) => <a key={i} href={img} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded-lg border bg-white overflow-hidden hover:shadow-md"><img src={img} alt="" className="w-full h-full object-contain p-1" /></a>)}</div>
            </div>
          )}
          {showStores && lookupResult.stores?.length > 0 && (
            <div className="p-3 bg-slate-50 rounded-lg border" data-testid="store-listings-panel">
              <div className="flex justify-between mb-2"><p className="text-sm font-semibold">Retail Stores</p><button type="button" onClick={() => setShowStores(false)}><X className="w-4 h-4 text-slate-400" /></button></div>
              <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b"><th className="text-left py-1.5 px-2 text-xs font-semibold text-slate-500">Store</th><th className="text-left py-1.5 px-2 text-xs font-semibold text-slate-500">Country</th><th className="text-left py-1.5 px-2 text-xs font-semibold text-slate-500">Price</th><th className="text-right py-1.5 px-2 text-xs font-semibold text-slate-500">Link</th></tr></thead>
                <tbody>{lookupResult.stores.map((s, i) => <tr key={i} className="border-b border-slate-100 last:border-0" data-testid={`store-row-${i}`}><td className="py-2 px-2 font-medium text-[#0F172A]">{s.storeName}</td><td className="py-2 px-2 text-slate-600">{s.storeCountry}</td><td className="py-2 px-2">{s.price ? `$${s.price}` : '-'}</td><td className="py-2 px-2 text-right">{s.link ? <a href={s.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#0052FF] font-medium">View <ExternalLink className="w-3 h-3" /></a> : '-'}</td></tr>)}</tbody></table></div>
            </div>
          )}
        </div>
      )}

      {/* Product Details + Pricing */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="product-details-section">
        <h3 className="font-semibold text-[#0F172A] mb-4">Product Details & Pricing</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-[#0F172A] mb-1">Batch *</label>
            <select value={form.batch_id} onChange={e => handleChange('batch_id', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="product-batch-select">
              <option value="">Select batch</option>
              {batches.map(b => <option key={b.batch_id} value={b.batch_id}>{b.batch_id} - {b.batch_name}</option>)}
            </select>
            {batches.length === 0 && <Link to="/batch-management" className="text-xs text-[#0052FF] mt-1 inline-block" data-testid="create-batch-link">Create batch first</Link>}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#0F172A] mb-1">Quantity *</label>
            <input value={form.quantity} onChange={e => handleChange('quantity', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="product-quantity-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#0F172A] mb-1">Retail Price</label>
            <input value={form.retailPrice} onChange={e => handleChange('retailPrice', e.target.value)} placeholder="$9.08" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="retail-price-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#0F172A] mb-1">Operation</label>
            <select value={form.operations} onChange={e => handleChange('operations', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="operation-select">
              <option value="">Select</option>
              <option value="percentage_addition">% Addition</option>
              <option value="flat_addition">Flat Addition</option>
              <option value="percentage_subtraction">Sale %</option>
              <option value="flat_substraction">Flat Subtraction</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#0F172A] mb-1">Value</label>
            <div className="relative">
              <input value={form.ourPriceValue} onChange={e => handleChange('ourPriceValue', e.target.value)} placeholder="25" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="price-value-input" />
              {(form.operations === 'percentage_subtraction' || form.operations === 'percentage_addition') && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">%</span>
              )}
            </div>
          </div>
          <div className="flex items-end">
            <button type="button" onClick={calculatePrice} className="w-full inline-flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg py-2 text-sm" data-testid="calculate-price-btn">
              <Calculator className="w-4 h-4" /> Calculate
            </button>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#0F172A] mb-1">Selling Price</label>
            <input value={form.finalPrice} onChange={e => handleChange('finalPrice', e.target.value)} placeholder="Calculated" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF] font-semibold" data-testid="final-price-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#0F172A] mb-1">Retail Price</label>
            <input value={form.compareAtPrice} onChange={e => handleChange('compareAtPrice', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="compare-at-input" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-[#0F172A]">Cost</label>
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="cost-mode-toggle">
                <button
                  type="button"
                  onClick={() => handleChange('costMode', '$')}
                  className={`px-2 py-0.5 text-[10px] font-bold transition-colors ${form.costMode === '$' ? 'bg-[#0052FF] text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                  data-testid="cost-mode-dollar"
                >$</button>
                <button
                  type="button"
                  onClick={() => handleChange('costMode', '%')}
                  className={`px-2 py-0.5 text-[10px] font-bold transition-colors ${form.costMode === '%' ? 'bg-[#0052FF] text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                  data-testid="cost-mode-percent"
                >%</button>
              </div>
            </div>
            {form.costMode === '%' ? (
              <div className="flex gap-1.5 items-center">
                <div className="relative flex-1">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={form.costPercent}
                    onChange={e => handleChange('costPercent', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF] pr-7"
                    placeholder="40"
                    data-testid="cost-percent-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">%</span>
                </div>
                <span className="text-sm font-semibold text-slate-700 whitespace-nowrap" data-testid="cost-percent-preview">= {form.cost || '$0'}</span>
              </div>
            ) : (
              <input value={form.cost} onChange={e => handleChange('cost', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="$0.00" data-testid="cost-input" />
            )}
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-xs font-medium text-[#0F172A] mb-1">Product Name</label>
            <input value={form.product_name} onChange={e => handleChange('product_name', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="product-name-input" />
          </div>
        </div>
      </div>

      {/* Action Tabs: Print / Shopify / Square */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="action-tabs-section">
        <div className="flex border-b border-slate-200 bg-slate-50">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold uppercase tracking-wide transition-colors ${activeTab === tab.key ? 'bg-white text-[#0052FF] border-b-2 border-[#0052FF]' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`tab-${tab.key}`}>
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>
        <div className="p-5">
          {/* Print Tab */}
          {activeTab === 'print' && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#0F172A] mb-1">Label Template</label>
                  <select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="template-select">
                    <option value="">Default Template</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div className="flex items-end">
                  <button type="button" onClick={handlePrintLabel} disabled={printLoading}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-lg py-2 text-sm disabled:opacity-50" data-testid="print-label-btn">
                    {printLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Print Label
                  </button>
                </div>
              </div>
              {labelPreview && (
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 bg-slate-50" data-testid="label-preview">
                  <p className="text-sm font-semibold text-[#0F172A] mb-3 text-center">Label Preview</p>
                  <div className="flex justify-center mb-4">
                    <img id="label-print-image" src={labelPreview} alt="Label Preview" className="border rounded-lg shadow-sm" style={{ maxHeight: 300, maxWidth: '100%' }} />
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {/* Print via browser */}
                    <button type="button" onClick={() => {
                      const tpl = templates.find(t => t.id === selectedTemplate);
                      const size = tpl ? tpl.size : '4x6';
                      const parts = size.split('x').map(Number);
                      const widthIn = parts[0];
                      const heightIn = parts[1];
                      
                      const printWin = window.open('', '_blank', `width=${widthIn * 96},height=${heightIn * 96}`);
                      printWin.document.write(`<!DOCTYPE html><html><head><title>Print Label</title><style>
                        @page { size: ${widthIn}in ${heightIn}in; margin: 0; }
                        * { margin: 0; padding: 0; }
                        html, body { width: ${widthIn}in; height: ${heightIn}in; overflow: hidden; }
                        img { width: ${widthIn}in; height: ${heightIn}in; display: block; }
                      </style></head><body><img src="${labelPreview}" onload="window.print();window.close();" /></body></html>`);
                      printWin.document.close();
                    }}
                      className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-lg px-6 py-2.5 text-sm" data-testid="print-preview-btn">
                      <Printer className="w-4 h-4" /> Print Image
                    </button>

                    {/* Copy ZPL to clipboard for direct Zebra printing */}
                    {labelZpl && (
                      <button type="button" onClick={() => {
                        navigator.clipboard.writeText(labelZpl).then(() => {
                          setMessage({ type: 'success', text: 'ZPL copied! Paste into Zebra Setup Utilities or send to printer.' });
                        }).catch(() => {
                          // Fallback
                          const ta = document.createElement('textarea');
                          ta.value = labelZpl;
                          document.body.appendChild(ta);
                          ta.select();
                          document.execCommand('copy');
                          document.body.removeChild(ta);
                          setMessage({ type: 'success', text: 'ZPL copied!' });
                        });
                      }}
                        className="inline-flex items-center gap-2 bg-[#0F172A] hover:bg-slate-800 text-white font-medium rounded-lg px-6 py-2.5 text-sm" data-testid="copy-zpl-btn">
                        <Tag className="w-4 h-4" /> Copy ZPL
                      </button>
                    )}

                    {/* Download ZPL file */}
                    {labelZpl && (
                      <button type="button" onClick={() => {
                        const blob = new Blob([labelZpl], { type: 'application/octet-stream' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'label.zpl';
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                        className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-[#0F172A] border border-slate-300 font-medium rounded-lg px-6 py-2.5 text-sm" data-testid="download-zpl-btn">
                        <ExternalLink className="w-4 h-4" /> Download ZPL
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 text-center mt-3">
                    For best results with Zebra: Copy ZPL → paste into Zebra Setup Utilities → Send to Printer
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Shopify Tab */}
          {activeTab === 'shopify' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Product Type</label>
                  {shopifyCategories.length > 0 ? (
                    <select value={form.productType} onChange={e => handleChange('productType', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="shopify-product-type">
                      <option value="">Select type...</option>
                      {shopifyCategories.map((t, i) => <option key={i} value={t}>{t}</option>)}
                    </select>
                  ) : (
                    <input value={form.productType} onChange={e => handleChange('productType', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="e.g. Beverages" data-testid="shopify-product-type" />
                  )}
                </div>
                <div><label className="block text-xs font-medium mb-1">Weight (lbs)</label><input value={form.weight} onChange={e => handleChange('weight', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="shopify-weight" /></div>
                <div><label className="block text-xs font-medium mb-1">Vendor *</label><input value={form.vendor} onChange={e => handleChange('vendor', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="shopify-vendor" /></div>
                <div><label className="block text-xs font-medium mb-1">SKU *</label><input value={form.sku} onChange={e => handleChange('sku', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="shopify-sku" /></div>
                <div>
                  <label className="block text-xs font-medium mb-1">Shipping Profile</label>
                  <select value={form.shopifyShippingProfile} onChange={e => handleChange('shopifyShippingProfile', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="shopify-shipping-profile">
                    <option value="">Default shipping</option>
                    {shopifyShippingProfiles.map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Physical product?</label>
                  <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white cursor-pointer">
                    <input type="checkbox" checked={form.requiresShipping}
                      onChange={e => setForm(prev => ({ ...prev, requiresShipping: e.target.checked, localPickupOnly: e.target.checked ? prev.localPickupOnly : false }))}
                      className="w-4 h-4 rounded border-slate-300 text-[#0052FF] focus:ring-[#0052FF]"
                      data-testid="shopify-physical-toggle" />
                    <span className="text-sm font-medium text-slate-700">{form.requiresShipping ? 'Yes — requires shipping' : 'Digital / Service'}</span>
                  </label>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Local Pickup Only?</label>
                  <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 cursor-pointer"
                    title="Keeps the product physical & taxable (POS charges tax normally) but removes all shipping rates at checkout — customers can only choose store pickup. Requires Local Pickup enabled in Shopify → Settings → Shipping and delivery.">
                    <input type="checkbox" checked={form.localPickupOnly}
                      onChange={e => setForm(prev => ({ ...prev, localPickupOnly: e.target.checked, requiresShipping: e.target.checked ? true : prev.requiresShipping }))}
                      className="w-4 h-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                      data-testid="shopify-local-pickup-toggle" />
                    <span className="text-sm font-medium text-amber-900">{form.localPickupOnly ? 'Pickup only — no shipping' : 'Ships normally'}</span>
                  </label>
                </div>
                <div className="col-span-2 sm:col-span-1"><label className="block text-xs font-medium mb-1">Description</label><textarea value={form.product_desc} onChange={e => handleChange('product_desc', e.target.value)} rows={1} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF] resize-none" data-testid="shopify-desc" /></div>
              </div>
              {/* Sales Channels */}
              {shopifySalesChannels.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mt-2">
                  <h4 className="text-xs font-semibold text-[#0F172A] uppercase tracking-wide mb-2">Sales Channels</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {shopifySalesChannels.map(ch => (
                      <label key={ch.id} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-200 cursor-pointer hover:border-[#96bf48] transition-colors" data-testid={`channel-${ch.id}`}>
                        <input type="checkbox" checked={!!selectedChannels[ch.id]}
                          onChange={e => setSelectedChannels(prev => ({ ...prev, [ch.id]: e.target.checked }))}
                          className="w-4 h-4 rounded border-slate-300 text-[#96bf48] focus:ring-[#96bf48]" />
                        <span className="text-xs font-medium text-[#0F172A]">{ch.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <button type="button" onClick={handlePushShopify} disabled={pushLoading}
                className="inline-flex items-center gap-2 bg-[#96bf48] hover:bg-[#7da33d] text-white font-medium rounded-lg px-5 py-2 text-sm disabled:opacity-50" data-testid="push-shopify-btn">
                {pushLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />} Push to Shopify
              </button>
            </div>
          )}

          {/* Square Tab */}
          {activeTab === 'square' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Category</label>
                  <select value={form.squareCategory} onChange={e => handleChange('squareCategory', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="square-category">
                    <option value="">Select category...</option>
                    {squareCategories.map((cat, i) => <option key={i} value={cat.id}>{cat.name}</option>)}
                  </select>
                </div>
                <div><label className="block text-xs font-medium mb-1">Description</label><textarea value={form.product_desc} onChange={e => handleChange('product_desc', e.target.value)} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF] resize-none" data-testid="square-desc" /></div>
                <div><label className="block text-xs font-medium mb-1">Quantity</label><input value={form.quantity} onChange={e => handleChange('quantity', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="square-qty" /></div>
                <div><label className="block text-xs font-medium mb-1">Weight (lbs)</label><input value={form.weight} onChange={e => handleChange('weight', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="0.0" data-testid="square-weight" /></div>
                <div><label className="block text-xs font-medium mb-1">SKU</label><input value={form.sku} onChange={e => handleChange('sku', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="Auto from barcode" data-testid="square-sku" /></div>
              </div>
              <button type="button" onClick={handlePushSquare} disabled={pushLoading}
                className="inline-flex items-center gap-2 bg-black hover:bg-slate-800 text-white font-medium rounded-lg px-5 py-2 text-sm disabled:opacity-50" data-testid="push-square-btn">
                {pushLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Store className="w-4 h-4" />} Push to Square
              </button>
            </div>
          )}

          {/* eBay Tab */}
          {activeTab === 'ebay' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Category *</label>
                  <select value={form.ebayCategory} onChange={async (e) => {
                    handleChange('ebayCategory', e.target.value);
                    await loadEbayAspects(e.target.value);
                  }} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="ebay-category">
                    <option value="">Search below to find category...</option>
                    {ebayCategories.map((cat, i) => <option key={i} value={cat.id}>{cat.name}</option>)}
                  </select>
                  <div className="flex gap-1 mt-1">
                    <input value={ebaySearchQuery} onChange={e => setEbaySearchQuery(e.target.value)} placeholder="e.g. ink cartridge, phone case..." className="flex-1 border border-slate-300 rounded-lg px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-[#0052FF]" data-testid="ebay-category-search"
                      onKeyDown={async (e) => { if (e.key === 'Enter') { e.preventDefault(); if (!ebaySearchQuery.trim()) return; try { const res = await apiCall(`/api/ebay-categories?q=${encodeURIComponent(ebaySearchQuery)}`, { method: 'GET' }); setEbayCategories(res.data || []); if (res.data?.length) handleChange('ebayCategory', res.data[0].id); } catch {} }}} />
                    <button type="button" onClick={async () => {
                      const q = ebaySearchQuery.trim() || form.product_name;
                      if (!q) return;
                      if (!ebaySearchQuery.trim()) setEbaySearchQuery(q);
                      try {
                        const res = await apiCall(`/api/ebay-categories?q=${encodeURIComponent(q)}`, { method: 'GET' });
                        setEbayCategories(res.data || []);
                        if (res.data?.length) {
                          handleChange('ebayCategory', res.data[0].id);
                          await loadEbayAspects(res.data[0].id);
                        }
                      } catch {}
                    }} className="px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium" data-testid="ebay-category-search-btn">Search</button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Condition</label>
                  <select value={form.ebayCondition} onChange={e => handleChange('ebayCondition', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="ebay-condition">
                    <option value="NEW">New</option>
                    <option value="LIKE_NEW">Like New</option>
                    <option value="NEW_OTHER">New Other</option>
                    <option value="USED_EXCELLENT">Used - Excellent</option>
                    <option value="USED_GOOD">Used - Good</option>
                    <option value="USED_ACCEPTABLE">Used - Acceptable</option>
                  </select>
                </div>
                <div><label className="block text-xs font-medium mb-1">SKU</label><input value={form.sku} onChange={e => handleChange('sku', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="Auto from barcode" data-testid="ebay-sku" /></div>
                <div><label className="block text-xs font-medium mb-1">Type *</label><input value={form.ebayType} onChange={e => handleChange('ebayType', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="e.g. Ink Cartridge" data-testid="ebay-type" /></div>
                <div><label className="block text-xs font-medium mb-1">Brand</label><input value={form.ebayBrand} onChange={e => handleChange('ebayBrand', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="e.g. HP, Samsung" data-testid="ebay-brand" /></div>
                <div><label className="block text-xs font-medium mb-1">Color</label><input value={form.ebayColor} onChange={e => handleChange('ebayColor', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="e.g. Black, Red" data-testid="ebay-color" /></div>
                <div><label className="block text-xs font-medium mb-1">Model</label><input value={form.ebayModel} onChange={e => handleChange('ebayModel', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="e.g. 564, Galaxy S24" data-testid="ebay-model" /></div>
                <div><label className="block text-xs font-medium mb-1">Quantity</label><input value={form.quantity} onChange={e => handleChange('quantity', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="ebay-qty" /></div>
                <div className="col-span-2 sm:col-span-1"><label className="block text-xs font-medium mb-1">Description</label><textarea value={form.product_desc} onChange={e => handleChange('product_desc', e.target.value)} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF] resize-none" data-testid="ebay-desc" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <label className="block text-xs font-medium mb-1">Shipping Cost ($)</label>
                  <input value={form.ebayShippingCost} onChange={e => handleChange('ebayShippingCost', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="0.00" data-testid="ebay-shipping-cost" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Returns</label>
                  <select value={form.ebayReturns ? 'yes' : 'no'} onChange={e => handleChange('ebayReturns', e.target.value === 'yes')} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="ebay-returns">
                    <option value="yes">Accepted</option>
                    <option value="no">Not Accepted</option>
                  </select>
                </div>
                {form.ebayReturns && (
                <div>
                  <label className="block text-xs font-medium mb-1">Return Within</label>
                  <select value={form.ebayReturnDays} onChange={e => handleChange('ebayReturnDays', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="ebay-return-days">
                    <option value="14">14 Days</option>
                    <option value="30">30 Days</option>
                    <option value="60">60 Days</option>
                  </select>
                </div>
                )}
              </div>

              {/* Shipping Details */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mt-2">
                <h4 className="text-xs font-semibold text-[#0F172A] uppercase tracking-wide mb-2">Shipping</h4>
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-4 sm:col-span-2">
                    <label className="block text-xs font-medium mb-1">Shipping Method</label>
                    <select value={form.ebayShippingMethod} onChange={e => handleChange('ebayShippingMethod', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" data-testid="ebay-shipping-method">
                      <option value="USPSMedia">USPS Media Mail</option>
                      <option value="USPSPriority">USPS Priority</option>
                      <option value="USPSFirstClass">USPS First Class</option>
                      <option value="UPSGround">UPS Ground</option>
                      <option value="UPS3rdDay">UPS 3 Day Select</option>
                      <option value="FedExHomeDelivery">FedEx Home Delivery</option>
                      <option value="FedExGround">FedEx Ground</option>
                      <option value="ShippingMethodFreight">Freight</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Weight (lbs)</label>
                    <input value={form.ebayWeightLbs} onChange={e => handleChange('ebayWeightLbs', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="lbs" data-testid="ebay-weight-lbs" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Weight (oz)</label>
                    <input value={form.ebayWeightOz} onChange={e => handleChange('ebayWeightOz', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="oz" data-testid="ebay-weight-oz" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <div><label className="block text-xs font-medium mb-1">Length (in)</label><input value={form.ebayDimL} onChange={e => handleChange('ebayDimL', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="in." data-testid="ebay-dim-l" /></div>
                  <div><label className="block text-xs font-medium mb-1">Width (in)</label><input value={form.ebayDimW} onChange={e => handleChange('ebayDimW', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="in." data-testid="ebay-dim-w" /></div>
                  <div><label className="block text-xs font-medium mb-1">Height (in)</label><input value={form.ebayDimH} onChange={e => handleChange('ebayDimH', e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0052FF]" placeholder="in." data-testid="ebay-dim-h" /></div>
                </div>
              </div>

              {/* Item Specifics — from eBay category requirements */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-[#0F172A] uppercase tracking-wide">Item Specifics {ebayAspects.length > 0 && <span className="text-slate-400 font-normal normal-case">({ebayAspects.filter(a=>a.required).length} required)</span>}</h4>
                  <button type="button" onClick={() => handleChange('ebayItemSpecifics', [...(form.ebayItemSpecifics || []), { name: '', value: '', options: [] }])}
                    className="text-xs font-medium px-2 py-1 bg-white border border-slate-300 hover:bg-slate-100 rounded-lg" data-testid="ebay-add-specific">+ Add Custom</button>
                </div>
                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                  {(form.ebayItemSpecifics || []).map((spec, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <div className="flex-1">
                        {spec.options === undefined || spec.options?.length === 0 ? (
                          <input value={spec.name} onChange={e => {
                            const updated = [...form.ebayItemSpecifics];
                            updated[i] = { ...updated[i], name: e.target.value };
                            handleChange('ebayItemSpecifics', updated);
                          }} className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-[#0052FF]" placeholder="Name" />
                        ) : (
                          <span className="text-xs font-medium text-[#0F172A] px-1">{spec.name} {spec.required && <span className="text-red-500">*</span>}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        {spec.options?.length > 0 ? (
                          <>
                            <input
                              value={spec.value}
                              onChange={e => {
                                const updated = [...form.ebayItemSpecifics];
                                updated[i] = { ...updated[i], value: e.target.value };
                                handleChange('ebayItemSpecifics', updated);
                              }}
                              list={`ebay-spec-${i}`}
                              placeholder="Type to search..."
                              className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-[#0052FF] bg-white"
                              data-testid={`ebay-spec-value-${i}`}
                            />
                            <datalist id={`ebay-spec-${i}`}>
                              {spec.options.map((opt, j) => <option key={j} value={opt} />)}
                            </datalist>
                          </>
                        ) : (
                          <input value={spec.value} onChange={e => {
                            const updated = [...form.ebayItemSpecifics];
                            updated[i] = { ...updated[i], value: e.target.value };
                            handleChange('ebayItemSpecifics', updated);
                          }} className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-[#0052FF]" placeholder="Value" />
                        )}
                      </div>
                      <button type="button" onClick={() => {
                        const updated = form.ebayItemSpecifics.filter((_, idx) => idx !== i);
                        handleChange('ebayItemSpecifics', updated.length ? updated : [{ name: '', value: '', options: [] }]);
                      }} className="text-red-400 hover:text-red-600 text-xs px-1">X</button>
                    </div>
                  ))}
                </div>
                {ebayAspects.length === 0 && form.ebayCategory && <p className="text-xs text-slate-400 mt-1">Select a category to load required item specifics.</p>}
              </div>
              {/* eBay Sold Price Research */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-1">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-[#0F172A] uppercase tracking-wide">eBay Market Research</h4>
                  <button type="button" disabled={ebaySoldLoading || !form.product_name} onClick={async () => {
                    setEbaySoldLoading(true); setEbaySoldData(null);
                    try {
                      const res = await apiCall(`/api/ebay-sold-prices?q=${encodeURIComponent(form.product_name)}`, { method: 'GET' });
                      setEbaySoldData(res.data);
                    } catch {} finally { setEbaySoldLoading(false); }
                  }} className="text-xs font-medium px-3 py-1.5 bg-[#e53238] hover:bg-[#cc2d32] text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-1" data-testid="ebay-sold-lookup">
                    {ebaySoldLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Tag className="w-3 h-3" />}
                    {ebaySoldLoading ? 'Searching...' : 'Check Sold Prices'}
                  </button>
                </div>
                {ebaySoldData && ebaySoldData.found && (
                  <div>
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      <div className="bg-white rounded-lg p-2 text-center border">
                        <p className="text-[10px] text-slate-400 uppercase">Avg Price</p>
                        <p className="text-lg font-bold text-green-600">${ebaySoldData.avg_price}</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 text-center border">
                        <p className="text-[10px] text-slate-400 uppercase">Median</p>
                        <p className="text-lg font-bold text-[#0052FF]">${ebaySoldData.median_price}</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 text-center border">
                        <p className="text-[10px] text-slate-400 uppercase">Low</p>
                        <p className="text-lg font-bold text-slate-600">${ebaySoldData.min_price}</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 text-center border">
                        <p className="text-[10px] text-slate-400 uppercase">High</p>
                        <p className="text-lg font-bold text-slate-600">${ebaySoldData.max_price}</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-400 mb-2">Based on {ebaySoldData.total_sold} recent sold listings</p>
                    {ebaySoldData.recent_sales?.length > 0 && (
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {ebaySoldData.recent_sales.map((sale, i) => {
                          const rowContent = (
                            <>
                              {sale.image && <img src={sale.image} alt="" className="w-8 h-8 rounded object-cover" />}
                              <div className="flex-1 min-w-0">
                                <p className="truncate text-slate-700 group-hover:text-[#0052FF] group-hover:underline">{sale.title}</p>
                                <p className="text-slate-400">{sale.end_date}</p>
                              </div>
                              <span className="font-bold text-green-600 whitespace-nowrap">${sale.sold_price.toFixed(2)}</span>
                              {sale.url && <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-[#0052FF]" />}
                            </>
                          );
                          return sale.url ? (
                            <a
                              key={i}
                              href={sale.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group flex items-center gap-2 bg-white rounded-lg p-1.5 border text-xs hover:bg-slate-50 hover:border-[#0052FF]/40 transition-colors"
                              data-testid={`ebay-sold-row-${i}`}
                            >
                              {rowContent}
                            </a>
                          ) : (
                            <div key={i} className="group flex items-center gap-2 bg-white rounded-lg p-1.5 border text-xs">
                              {rowContent}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {ebaySoldData && ebaySoldData.found && ebaySoldData.sold_search_url && (
                  <a href={ebaySoldData.sold_search_url} target="_blank" rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0052FF] hover:underline"
                    data-testid="ebay-sold-search-link">
                    Verify all sold results on eBay <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {ebaySoldData && !ebaySoldData.found && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500">{ebaySoldData.message}</p>
                    {ebaySoldData.sold_search_url && (
                      <a href={ebaySoldData.sold_search_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#e53238] text-white hover:bg-[#cc2d32]"
                        data-testid="ebay-open-sold-search">
                        Open eBay Sold Search <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                )}
                {!ebaySoldData && !ebaySoldLoading && (
                  <p className="text-xs text-slate-400">Enter a product name and click "Check Sold Prices" to see what similar items sell for on eBay.</p>
                )}
              </div>

              <button type="button" onClick={handlePushEbay} disabled={pushLoading}
                className="inline-flex items-center gap-2 bg-[#e53238] hover:bg-[#cc2d32] text-white font-medium rounded-lg px-5 py-2 text-sm disabled:opacity-50" data-testid="push-ebay-btn">
                {pushLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />} Push to eBay
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
